import * as Sentry from '@sentry/react';

import { gitSha } from '../../constants/git-data';

const httpStatusesToIgnore = [401, 403, 404, 503];
const MAX_CAUSE_DEPTH = 10;
const NETWORK_ERROR_MESSAGE = 'Network Error';
const CDN_URL_FRAGMENT = 'cdn.shipstation';
const IGNORABLE_AXIOS_CODES = new Set(['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT']);

const findAxiosError = (error) => {
  let current = error;
  let depth = 0;
  while (current && depth < MAX_CAUSE_DEPTH) {
    if (current.isAxiosError === true && current.response?.status !== undefined) return current;
    current = current.cause;
    depth++;
  }
  return null;
};

const isIgnorableAxiosCode = (error) => {
  let current = error;
  let depth = 0;
  while (current && depth < MAX_CAUSE_DEPTH) {
    if (current.isAxiosError === true && (IGNORABLE_AXIOS_CODES.has(current.code) || current.message === NETWORK_ERROR_MESSAGE))
      return true;
    current = current.cause;
    depth++;
  }
  return false;
};

const isAxiosErrorToIgnore = (hint) => {
  try {
    const error = hint.originalException;
    if (!error) return false;

    if (isIgnorableAxiosCode(error)) return true;

    const axiosError = findAxiosError(error);
    if (!axiosError) return false;

    const { status } = axiosError.response;
    if (status === 404 && axiosError.config?.url?.includes(CDN_URL_FRAGMENT)) {
      return false;
    }
    return httpStatusesToIgnore.includes(status);
  } catch {
    return false;
  }
};

const isBrowserExtensionErrorToIgnore = (event) => {
  try {
    return event.extra.__serialized__.detail.reason.message === 'Extension context invalidated.';
  } catch {
    return false;
  }
};

const isNonNativeException = (event) => {
  try {
    const stackTraceFrames = event.exception.values[0].stacktrace.frames;
    return !stackTraceFrames[stackTraceFrames.length - 1].abs_path.includes('shipstation');
  } catch {
    return false;
  }
};

if (window && window.SS_GLOBALS && window.SS_GLOBALS.frontEndConfig) {
  Sentry.init({
    sendDefaultPii: true,
    normalizeDepth: 10,
    dsn: window.SS_GLOBALS.frontEndConfig.SentryDsn, // it should be noted that alerts are keyed to the prod `shipstation-v3` project
    environment: window.SS_GLOBALS.frontEndConfig.serverEnv ? `${window.SS_GLOBALS.frontEndConfig.serverEnv}` : undefined,
    release:
      process.env.RELEASE_VERSION && process.env.RELEASE_VERSION.length
        ? `${process.env.SENTRY_PROJECT}@${process.env.RELEASE_VERSION}`
        : undefined,
    ignoreErrors: [
      'NetworkError',
      'Error: Network Error',
      'Failed to fetch',
      'Load failed',
      'Script error.',
      'ResizeObserver loop completed with undelivered notifications',
      'ResizeObserver loop limit exceeded',
      'ChunkLoadError',
      /Loading chunk [\d]+ failed/,
      'timeout of 0ms exceeded',
      'should_do_lastpass_here',
      'Non-Error exception',
      'Non-Error promise rejection',
      "Unable to get property 'campaign' of undefined or null reference",
      'Failed to preload Sierra script:',
      'Sierra Chat initialization failed:',
      'Sierra Chat failed, falling back to Sunco:',
      '__evt_name',
    ],
    beforeSend(event, hint) {
      if (isAxiosErrorToIgnore(hint)) {
        return null;
      }
      if (isBrowserExtensionErrorToIgnore(event)) {
        return null;
      }
      if (isNonNativeException(event)) {
        return null;
      }

      return event;
    },
    denyUrls: [/^moz-extension:\//i, /^chrome:\/\//i, /^chrome-extension:\//i, /^safari-extension:\//i, /^safari-web-extension:\//i],
    allowUrls: [/shipstation/i, /cloudfront/i],
    initialScope: {
      tags: {
        frontendVersionSha: gitSha,
      },
    },
  });
}

class SentryServiceClass {
  identifyUser(id, sellerId, userInfo) {
    const scope = Sentry.getCurrentScope();
    scope.setUser({
      id,
      sellerId,
      ...userInfo,
    });
    scope.setTag('app-seller-id', sellerId);
  }

  trackPage(pageName) {
    Sentry.addBreadcrumb({
      category: 'page',
      message: pageName,
      level: 'info',
    });
  }

  trackEvent(message, data) {
    Sentry.addBreadcrumb({
      category: 'redux',
      message,
      data,
      level: 'info',
    });
  }
}

export const SentryService = new SentryServiceClass();
